<template>
    <div class="p-12 container">
        <h2>
            {{ viewingPaymentMethods ? 'Payment Methods' : 'Review Order' }}
        </h2>

        <ul class="breadcrumb bg-grey-100 rounded p-4 mb-8">
            <li class="breadcrumb-item"><router-link to="/order">Order</router-link></li>
            <li class="breadcrumb-item active">Review Order</li>
        </ul>

        <div class="flex flex-wrap">
            <div class="w-full pb-8 md:w-2/3 md:pr-4 overflow-hidden">
                <transition mode="out-in" enter-active-class="animated slideInRight" leave-active-class="animated slideOutRight">
                    <div v-if="viewingPaymentMethods" key="paymentView" style="animation-duration: 0.5s">
                        <p class="mb-1">We accept Credit Card payments secured by Stripe, PayPal, Direct Debit, and BPAY.</p>
                        <p>Please <a href="/support" target="_blank" title="Contact Us">contact us</a> if you need to pay by Purchase Order or Invoice.</p>

                        <sau-utility-payment-methods></sau-utility-payment-methods>

                    </div>

                    <div v-else key="orderView" style="animation-duration: 0.5s">
                        <template v-if="! orderIsLoaded">
                            <sau-loading>
                                <template slot="message">Fetching Order</template>
                            </sau-loading>
                        </template>

                        <template v-else>
                            <div class="table-responsive">
                                <table class="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <td>
                                                <h5 class="sau-pm-reset">Description</h5>
                                            </td>
                                            <td align="right">
                                                <h5 class="sau-pm-reset">Amount</h5>
                                            </td>
                                        </tr>
                                        <tr v-for="(item, index) in order.items" :key="index">
                                            <td>
                                                <p class="mb-2"><template v-if="item.type == 'vps'">Cloud Server</template><template v-else>{{ item.type | Ucwords }}</template>
                                                    <template v-if="!item.readyToOrder">
                                                        <span class="float-right" tabindex="0" data-toggle="tooltip" :title="formatStillRequiredFields(item)">
                                                            <span class="badge badge-info" :class="{
                                                                'border-none bg-orange-100 text-orange': true,
                                                                'border border-orange bg-white text-orange': false,
                                                            }">Needs More Information</span>
                                                        </span>
                                                    </template>
                                                    <template v-if="item.type === 'dedicated-server' && item.rtg">
                                                        <span class="float-right mr-2" tabindex="0" data-toggle="tooltip" title="This is a 'Ready to Go' server and will be ready to use in 7 minutes or less">
                                                            <span class="badge badge-success" :class="{
                                                                'border-none bg-green-100 text-green': true,
                                                                'border border-green bg-white text-green': false,
                                                            }">Ready to Go Server</span>
                                                        </span>
                                                    </template>
                                                </p>

                                                <ul class="list-unstyled text-sm mb-2">

                                                    <template v-if="item.type === 'vps'">
                                                        <li>
                                                            <span class="text-grey-600">Description: </span> <p class="mb-0" v-html="item.description"></p>
                                                            <span class="text-grey-600">Operating System:</span> {{ item.os.name }}<br />
                                                            <span class="text-grey-600">IP Addresses:</span> {{ item.ips.name }}
                                                            <template v-if="item.hostname">
                                                                <br />
                                                                <span class="text-grey-600">Hostname: </span>{{ item.hostname }}
                                                            </template>
                                                            <template v-if="item.licence && item.licence.description">
                                                                <br />
                                                                <span class="text-grey-600">Licence: </span>{{ item.licence.description }}
                                                            </template>
                                                            <template v-if="item.addons && item.addons.management">
                                                                <br />
                                                                <span class="text-grey-600">Management: </span>{{ item.addons.management.name }}
                                                            </template>
                                                        </li>
                                                    </template>

                                                    <template v-else-if="item.type === 'domain' || item.type === 'domain-transfer' || item.type === 'domain-renewal'">
                                                        <li>
                                                            <span class="text-grey-600">Domain Name:</span> {{ item.domain }}
                                                        </li>
                                                        <li>
                                                            <span class="text-grey-600">Registration Period:</span> {{ item.registration_period }} {{ item.registration_period > 1 ? 'years' : 'year' }}
                                                        </li>
                                                    </template>

                                                    <template v-else-if="item.type === 'managed-wordpress'">
                                                        <li>
                                                            <span class="text-grey-600">Package:</span> {{ item.package }}
                                                        </li>
                                                        <li>
                                                            <span class="text-grey-600">Domain:</span> {{ item.domain }}
                                                        </li>
                                                    </template>

                                                    <template v-else-if="item.type === 'mega-bundle'">

                                                        <li v-if="item.package">
                                                            <span class="text-grey-600">CPU:</span> {{ item.package.cpu }}
                                                        </li>
                                                        <li v-if="item.package">
                                                            <span class="text-grey-600">RAM:</span> {{ item.package.ram }}
                                                        </li>
                                                        <li v-if="item.package">
                                                            <span class="text-grey-600">Storage:</span> {{ item.package.storage }}
                                                        </li>
                                                        <li v-if="item.package">
                                                            <span class="text-grey-600">Backups:</span> {{ item.package.backup }}
                                                        </li>
                                                        <li v-if="item.package">
                                                            <span class="text-grey-600">Bandwidth:</span> {{ item.package.bandwidth }}
                                                        </li>
                                                        <li v-if="item.option">
                                                            <span class="text-grey-600">Bundle:</span> {{ item.option.name }}
                                                        </li>
                                                        <li v-if="item.option">
                                                            <span class="text-grey-600">Location:</span> {{ item.option.location }}
                                                        </li>
                                                        <li v-if="item.option">
                                                            <span class="text-grey-600">Management:</span> {{ item.option.management }}
                                                        </li>
                                                        <li v-if="item.option">
                                                            <span class="text-grey-600">Monitoring:</span> {{ item.option.monitoring }}
                                                        </li>
                                                        <li v-if="item.option">
                                                            <span class="text-grey-600">Software:</span> {{ item.option.software }}
                                                        </li>

                                                    </template>

                                                    <template v-else-if="item.type === 'colocation'">
                                                        <li>
                                                            <span class="text-grey-600">Data Centre:</span> {{ item.dc_location.name }}
                                                        </li>
                                                        <li>
                                                            <span class="text-grey-600">Allocation:</span> {{ item.allocation.title }}
                                                        </li>
                                                    </template>

                                                    <template v-else-if="item.type === 'ssl-certificate'">
                                                        <li>
                                                            <span class="text-grey-600">Product:</span> {{ data.config.products.sslcertificate.find(p => p.id == item.product_id).name }}
                                                        </li>
                                                        <li>
                                                            <span class="text-grey-600">Domain:</span> {{ item.domain ? item.domain : 'Not Provided' }}
                                                        </li>
                                                        <li>
                                                            <span class="text-grey-600">Billing Cycle:</span> {{ item.billing_cycle_years }} {{ item.billing_cycle_years == 1 ? 'Year' : 'Years' }}
                                                        </li>
                                                    </template>

                                                    <template v-else-if="item.type === 'dedicated-server'">
                                                        <li v-if="item.slug">
                                                            <span class="text-grey-600">
                                                                <template v-if="item.type === 'dedicated-server'">Server ID:</template>
                                                                <template v-else>ID:</template>
                                                            </span> {{ item.slug }}
                                                        </li>
                                                        <li v-if="item.os">
                                                            <span class="text-grey-600">Operating System:</span> {{ item.os.description }}
                                                        </li>
                                                        <li v-if="item.addons && item.addons.windows_server_licence">
                                                            <span class="text-grey-600">Windows Licence:</span> {{ item.addons.windows_server_licence.name }}
                                                        </li>
                                                        <li v-if="item.location">
                                                            <span class="text-grey-600">Location:</span> {{ item.location.description}}
                                                        </li>
                                                        <li v-if="item.hostname">
                                                            <span class="text-grey-600">Hostname:</span> {{ item.hostname }}
                                                        </li>
                                                        <li v-if="item.cpu">
                                                            <span class="text-grey-600">CPU:</span> {{ item.cpu.description }}
                                                        </li>
                                                        <li v-if="item.ram">
                                                            <span class="text-grey-600">RAM:</span> {{ item.ram.description }}
                                                        </li>
                                                        <li v-if="item.bandwidth">
                                                            <span class="text-grey-600">Bandwidth:</span> {{ item.bandwidth.description }}
                                                        </li>
                                                        <li v-if="item.storage.length">
                                                            <span class="text-grey-600">Storage: </span> <span v-for="(disk, index) in item.storage" :key="index">{{ disk.description }} </span>
                                                        </li>
                                                        <li v-if="item.addons && item.addons.backup_frequency">
                                                            <span class="text-grey-600">Backup Frequency:</span> {{ item.addons.backup_frequency.name }}
                                                        </li>
                                                        <li v-if="item.addons && item.addons.backup_storage">
                                                            <span class="text-grey-600">Backup Storage:</span> {{ item.addons.backup_storage.name }}
                                                        </li>
                                                        <li v-if="item.addons && item.addons.management">
                                                            <span class="text-grey-600">Management:</span> {{ item.addons.management.name }}
                                                        </li>
                                                        <li v-if="item.addons && item.addons.additional_ips">
                                                            <span class="text-grey-600">Additional IP's:</span> {{ item.addons.additional_ips.name }}
                                                        </li>
                                                        <li v-if="item.licence && item.licence.description">
                                                            <span class="text-grey-600">Licence:</span> {{ item.licence.description }}
                                                        </li>
                                                    </template>
                                                </ul>

                                                <button
                                                    v-if="itemIsEditable(item)"
                                                    @click.prevent="editItem(item)"
                                                    class="text-xs text-green hover:text-green-600 mr-2"
                                                >
                                                    <i class="far fa-pencil"></i>
                                                    Edit
                                                </button>
                                                <button @click.prevent="removeItem(item)" class="text-xs text-red hover:text-red-600">
                                                    <i class="far fa-trash"></i>
                                                    Remove
                                                </button>
                                            </td>
                                            <td align="right" width="28%">
                                                {{ item.price.rrp | currency }}
                                            </td>
                                        </tr>

                                        <tr class="sau-font-bold" v-if="order.coupon">
                                            <td align="right">
                                                Discount
                                            </td>
                                            <td align="right" width="28%">
                                                -{{ order.coupon.discount | currency }}
                                            </td>
                                        </tr>
                                        <tr class="sau-font-bold">
                                            <td align="right">
                                                Sub Total
                                            </td>
                                            <td align="right" width="28%">
                                                {{ order.subtotalWithDiscount  | currency }}
                                            </td>
                                        </tr>
                                        <tr class="sau-font-bold" v-if="getUsersAppliedTaxName">
                                            <td align="right">Tax</td>
                                            <td align="right" width="28%">
                                                {{ order.tax | currency }}
                                            </td>
                                        </tr>
                                        <tr class="sau-font-bold sau-font lg">
                                            <td align="right">Total</td>
                                            <td align="right" width="28%">
                                                {{ order.total | currency }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                    </div>
                </transition>
            </div>

            <div class="w-full md:w-1/3 md:pl-4">



                <div
                    id="order-summary"
                    :class="{
                        'md:pb-8': this.sidebarHeight !== 'auto',
                        'md:border': this.sidebarHeight !== 'auto',
                        'md:overflow-y-scroll': this.sidebarHeight !== 'auto',
                    }"
                >

                    <div
                        class="border md:border-0 p-8 bg-white"
                        :class="{
                            'md:shadow-sau-lg': this.sidebarHeight === 'auto',
                        }"
                    >

                        <sau-request-additional-information v-if="$root.requiresAdditionalUserInformation"></sau-request-additional-information>

                        <template v-else>
                            <div class="">
                                <button
                                    @click.prevent="togglePaymentView()"
                                    class="block w-full p-2 mb-4 text-sm border text-grey hover:border-grey-600 hover:text-grey-600 border-solid"
                                    :class="{
                                        'border-red text-red hover:border-red-600 hover:text-red-600': ! paymentMethodIsSelected
                                    }"
                                >
                                    {{ viewingPaymentMethods ? 'Review Order' : (paymentMethodIsSelected ? 'Change Payment Method' : 'Select Payment Method') }}
                                </button>
                            </div>

                            <div class="border-t pt-3 mb-3 text-center">
                                <p class="text-xl w-full">
                                    Payment Method
                                </p>
                                <div>
                                    <transition name="fade" mode="out-in" v-if="ready" appear>
                                        <div :key="paymentMethodSelectedImage" style="animation-duration: 0.3s">
                                            <img
                                                height="35"
                                                alt="Payment Method Selected"
                                                v-if="paymentMethodIsSelected"
                                                :src="paymentMethodSelectedImage"
                                            />

                                            <div v-if="! paymentMethodIsSelected" class="text-red text-sm">
                                                You must select a payment method.
                                            </div>

                                            <div v-else-if="this.paymentMethodSelected == 'bpay'">
                                                <a href="#" id="bpay-popover" class="sau-font xs">View BPAY Biller Code</a>
                                                <b-popover target="bpay-popover"
                                                        placement="top"
                                                        triggers="hover focus">
                                                    <sau-utility-bpay></sau-utility-bpay>
                                                </b-popover>
                                            </div>

                                            <div v-else-if="this.paymentMethodSelected === 'creditcard'">
                                                <small>xxxx-xxxx-xxxx-{{ this.userDetails.billing.card_last_four  }}</small>
                                            </div>

                                            <div v-else>
                                                <small>{{ selectedPaymentMethodDetailsDisplay }}</small>
                                            </div>

                                        </div>
                                    </transition>
                                </div>
                            </div>



                            <div class="border-t pt-3">

                                <coupon :coupon="order.coupon" />

                                <p class="text-sm w-full mb-1">
                                    Sub Total
                                    <span class="float-right">{{ order.subtotalWithDiscount | currency }}</span>
                                </p>
                                <p class="text-sm w-full mb-1" v-if="getUsersAppliedTaxName">
                                    +{{ getUsersAppliedTaxRate }}% {{ getUsersAppliedTaxName }}
                                    <span class="float-right">{{ order.tax | currency }}</span>
                                </p>
                                <p class="text-xl w-full mb-0 sau-font-bold">
                                    Total
                                    <span class="float-right">{{ order.total | currency }}</span>
                                </p>
                            </div>

                            <div class="mt-3">
                                <b-form-checkbox v-model="agree" @change="registerToolTips()">
                                    <template v-if="agreeMessages.length === 1">
                                        <span v-html="`I agree to the ${agreeMessages[0]}.`" class="text-xs"></span>
                                    </template>
                                    <template v-else>
                                        <p class="text-xs mb-0 mt-1">I agree to the:</p>
                                        <ul class="text-xs">
                                            <li :key="index"
                                                v-html="agreeMessage"
                                                v-for="(agreeMessage, index) in agreeMessages"
                                            ></li>
                                        </ul>
                                    </template>
                                </b-form-checkbox>
                            </div>

                            <div class="text-center mt-3">
                                <template v-if="order.readyToOrder">
                                    <template v-if="!agree">
                                        <div v-b-tooltip.hover title="Please agree to the above terms to continue">
                                            <button class="btn btn-lg w-full block mb-4" :class="buttonColour()" @click="processOrder()" :disabled="! paymentIsAllowed">
                                                {{ this.paymentMethodSelected === 'credit' ? 'Apply Credit' : (syncRequestsPending || hasPendingItems ? 'Please wait...' : 'Pay Now') }}
                                            </button>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <button class="btn btn-lg w-full block mb-4" :class="buttonColour()" @click="processOrder()" :disabled="! paymentIsAllowed">
                                            {{ this.paymentMethodSelected === 'credit' ? 'Apply Credit' : (syncRequestsPending || hasPendingItems ? 'Please wait...' : 'Pay Now') }}
                                        </button>
                                    </template>
                                </template>
                                <template v-else>
                                    <span tabindex="0" data-toggle="tooltip" title="Some items in the order require your attention">
                                        <button class="btn btn-success w-100 mb-4" style="pointer-events: none;" type="button" disabled>Pay Now</button>
                                    </span>
                                </template>
                                <button @click="deleteOrder()" class="btn block w-full text-red hover:bg-red hover:text-red-100">Cancel Order</button>
                            </div>

                        </template>
                    </div>
                </div>
            </div>
        </div>
        <b-modal
            centered
            ref="bpayModal"
            ok-variant="success"
            title="Pay with BPAY"
            cancel-variant="light"
            @ok="handleBpayPayment"
            ok-title="Continue with BPAY"
        >
            <p>BPAY payments can take up to 3 business days to process. If you need to pay for your transaction urgently, please choose the Credit Card or PayPal options.</p>
            <p>Use the biller code and reference number to make a BPAY payment through your bank or financial institution.</p>
            <div class="row">
                <div class="col-lg-8 offset-lg-2">
                    <sau-utility-bpay></sau-utility-bpay>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Helpers from '@js/helpers';
import jsFunctions from '@js/store/jqueryFunctions';
import Coupon from '@js/components/order/coupon/Coupon';

export default {
    props: {
        data: Object,
        order: Object,
    },

    mixins: [jsFunctions],

    components: {
        'coupon' : Coupon,
    },

    data: function () {
        return {
            fraud: false,
            agree: false,
            error: `<div class="text-center">
                <img src="/img/apiro-broken-180.png" class="mx-auto mb-6"/>
                <h5 class="sau-pm-reset mb-2">There was an issue placing your order.</h5>
                <p class="mb-6">We'll contact you shortly, or if you like, you can contact us below.</p>
                <a href="/support" title="Contact Support" class="btn btn-primary">Contact Us</a>
            </div>`,
            ready: false,
            refreshing: false,
            processing: false,
            pendingRequests: 0,
            sidebarWidth: '100%',
            sidebarHeight: 'auto',
            viewingPaymentMethods: this.paymentView,
            terms: null,
        }
    },


    activated() {
        this.refreshOrderIfNeeded();
        // check if colo has been added to ensure they agree to the updated terms and conditions or just make them agree whenever they leave and come back to this page?
        this.agree = false;
        this.registerToolTips();
    },

    mounted() {

        this.$nextTick(function () {
            window.addEventListener('resize', this.keepSidebarWithinView);
            this.keepSidebarWithinView();
        });

        if (this.userFeatures.hasOwnProperty('selectedPaymentMethod')) {
            this.$store.commit('paymentMethodSelected', this.userFeatures.selectedPaymentMethod);
        }

        if(this.paymentMethodSelected === 'credit') {
            this.$store.commit('paymentMethodSelected', '');
        }

        // If the selected payment method is creditcard - make sure they have one on file
        if(this.paymentMethodSelected === 'creditcard' && !this.ccLastFour) {
            this.$store.commit('paymentMethodSelected', '');
        }

        this.getPaypalBAState().then(response => {
            this.$store.commit('paypalActiveBAOnFile', response.data.data.baState);
            this.$store.commit('paypalActiveBAEmail', response.data.data.baEmail);
            this.ready = true;
        });
        if (this.isOrderLoaded() && this.order.items.length === 0) {
            this.$router.push(`/order`);
        }

        window.bus.$on('coupon.removed', this.termsMustBeAccepted);
        window.bus.$on('coupon.applied', this.termsMustBeAccepted);
        window.bus.$on('order.items.created', this.termsMustBeAccepted);
        window.bus.$on('order.items.updated', this.termsMustBeAccepted);
        window.bus.$on('order.items.deleted', this.termsMustBeAccepted);
    },

    beforeDestroy: function () {
        window.removeEventListener('resize', this.keepSidebarWithinView);
    },

    computed: {

        paymentMethodIsSelected() {
            return !! this.paymentMethodSelected;
        },

        paymentMethodSelectedImage: function () {
            if (! this.ready) {
                return;
            }

            switch (this.paymentMethodSelected) {
                case "creditcard":
                    return this.getCreditCardImage();
                    break;
                case "paypal":
                    return '/img/logo-paypal.gif';
                    break;
                case "bpay":
                    return '/img/bpay-landscape.gif';
                    break;
                // case "credit":
                //     return '/img/account-credit.gif';
                //     break;
                default: '';
            }
        },

        selectedPaymentMethodDetailsDisplay: function () {
            switch (this.paymentMethodSelected) {
                case "creditcard":
                    return this.ccLastFour
                case "paypal":
                    return this.clientActivePaypalBAEmail
                case "bpay":
                    return this.userDetails.billing.bpay_ref ? this.userDetails.billing.bpay_ref : 'N/A'
                // case "credit":
                //     return this.userDetails.billing.credit
                default:
                    return 'No payment method selected';
            }
        },

        hasColocation() {
            return !_.isEmpty(this.order.items.filter(item => item.type == 'colocation'));
        },

        agreeMessages() {
            // Unique set of terms for all products on the order
            return [...new Set(
                Object.values(this.order.terms.product)
                    .map(p => Object.values(p))
                    .flat(2)),
                this.order.terms.general
            ];
        },

        paymentIsAllowed() {
            this.refreshOrderIfNeeded();

            return (this.syncRequestsPending === 0
                && this.paymentMethodIsSelected
                && this.agree
                && ! this.hasPendingItems
                && ! this.fraud);
        },

        syncRequestsPending() {
            return window.store.misc.syncRequestsPending;
        },

        orderIsLoaded() {
            return window.store.orders.loaded;
        },

        isReviewing() {
            return this.$route.path.startsWith('/order/view');
        },

        hasPendingItems() {
            return !! this.order.items.filter(item => item.slug.startsWith('temp-')).length > 0;
        },
    },
    methods: {
        refreshOrderIfNeeded() {

            if (this.isReviewing && (! this.order.isFresh || ! this.order.readyToOrder) && this.syncRequestsPending === 0 && ! this.refreshing) {
                this.refreshing = true;
                window.bus.$once('order.refreshed', () => this.refreshing = false);
                window.bus.$emit('order.refresh');
            }
        },

        buttonColour() {
            return this.paymentIsAllowed ? 'btn-success' : 'btn-light';
        },

        editItem(item) {
            window.bus.$emit('order.items.edit', item);
        },

        deleteOrder() {
            window.bus.$emit('order.delete');
        },

        togglePaymentView() {
            this.viewingPaymentMethods = ! this.viewingPaymentMethods;
            this.$store.commit('setPaymentView', this.viewingPaymentMethods);

            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });

        },

        handleBpayPayment() {
            this.processOrderWithBpayBypass();
        },

        removeItem(item) {
            window.bus.$once('order.items.deleted', response => {
                this.refreshOrderIfNeeded();
            });

            window.bus.$emit('order.items.delete', item);
        },

        processOrderWithBpayBypass() {
            return this.processOrder(true);
        },

        processOrder(bypassBpay = false) {
            let processingMessage = 'Processing your order';
            let payingWithBpay = this.paymentMethodSelected === 'bpay';
            let payingWithPaypal = this.paymentMethodSelected === 'paypal';
            // let payingWithCredit = this.paymentMethodSelected === 'credit';

            if (payingWithBpay && ! bypassBpay) {
                this.$refs.bpayModal.show();
                return;
            }

            this.processing = true;

            this.$swal({
                title: processingMessage,
                text: "Please wait...",
                imageUrl: "/img/spinner-dark-gray-80.gif",
                showConfirmButton: false,
                allowOutsideClick: false
            });

            window.bus.$once('order.processed', response => {
                if (response.data.status == 'success') {
                    this.$swal({
                        type: 'success',
                        title: "Success",
                        text: "Order has been successfully placed!",
                        showConfirmButton: false,
                        allowOutsideClick: true,
                        timer: 3000,
                        onClose: () => {
                            return this.$router.push('/order/complete');
                        }
                    });
                } else {
                    if (response.data.stage === 'order' || response.data.stage == 'fraud') {
                        this.fraud = true; // prevents additional submissions
                        this.$swal({
                            html: this.error,
                            showConfirmButton: false,
                            allowOutsideClick: true,
                            onClose: () => {
                                return window.location = '/support';
                            }
                        })
                    } else if (response.data.stage === 'payment') {
                        this.$swal({
                            type: 'info',
                            title: "Your order has been placed",
                            html: response.data.message,
                            showConfirmButton: false,
                            allowOutsideClick: true,
                            timer: 3000,
                            onClose: () => {
                                window.location = `/billing/invoices/${response.data.result.invoiceid}`;
                            }
                        });
                    } else if (response.data.error) {
                        this.$swal({
                            html: response.data.error,
                            type: 'error',
                            showConfirmButton: false,
                            allowOutsideClick: true,
                            onClose: () => {
                                return window.location.reload();
                            }
                        })
                    }
                }

                window.bus.$off('order.process-failed');
            });

            window.bus.$once('order.process-failed', error => {
                if (error.response.status === 422) {
                    if (this.hasQuestionableDomains().length) {
                        this.$router.push(`/order/confirm-domains`, function (whatIsThis) {
    
                        });
                        this.$swal({
                            type: 'warning',
                            html: `
                                <p>Please complete this form to continue with your purchase.</p>
                            `,
                            showConfirmButton: false,
                            cancelButtonClass: 'bg-white text-grey-900',
                            showCancelButton: true,
                        });
                        return;
                    }
                }

                this.$swal({
                    type: 'error',
                    title: "There was an issue placing your order",
                    text: 'Please try again or contact support for help.',
                    showConfirmButton: false,
                    allowOutsideClick: true,
                });

                window.bus.$off('order.processed');
            });

            window.bus.$emit('order.process');
        },

        keepSidebarWithinView(event) {
            let bottomMargin = 32; // 2rem
            let element = document.querySelector('#order-summary');
            let bounds = Helpers.outOfViewport(element);
            let innerHeightExceedsBounds = (element.scrollHeight + element.offsetTop) > window.innerHeight;

            if ((bounds.bottom || innerHeightExceedsBounds) && Helpers.screens().md) {
                this.sidebarHeight = `${window.innerHeight - element.offsetTop - bottomMargin}px`;
            } else {
                this.sidebarHeight = 'auto';
            }

            if (Helpers.screens().md) {
                let parentComputedStyle = window.getComputedStyle(element.parentElement);
                let xPaddingForParentElement = window.parseInt(parentComputedStyle.paddingLeft) + window.parseInt(parentComputedStyle.paddingRight);
                this.sidebarWidth = `${element.parentElement.clientWidth - xPaddingForParentElement}px`;
            } else {
                this.sidebarWidth = '100%';
            }

            // I don't know why this is being dumb...
            element.style = `
                width: ${this.sidebarWidth};
                min-width: ${this.sidebarWidth};
                max-width: ${this.sidebarWidth};
                max-height: ${this.sidebarHeight};
            `;
        },

        itemIsEditable(item) {
            if (item.type === 'domain') {
                return this.domainHasQuestions(item);
            }

            if (item.type === 'domain-transfer' || item.type === 'domain-renewal') {
                return false;
            }

            return true;
        },

        domainHasQuestions(item) {
            return item.questions
                && item.type === 'domain';
        },

        formatStillRequiredFields(item) {
            // Take the array of required fields and convert it to a clean string for display purposes, eg '..following: Cpu, Ram, ...'
            return (item.hasOwnProperty('stillRequired') ? 'Please provide the following: ' +  item.stillRequired.map(i => _.startCase(_.toLower(i))).join(', ') : '');
        },

        termsMustBeAccepted() {
            this.agree = false;
        },

        hasQuestionableDomains() {
            return this.order.items.filter(item => {
                return item.type === 'domain' && item.questions;
            });
        }
    },

    watch: {
        order: function (val) {
            // this.refreshOrderIfNeeded();

            if (this.isOrderLoaded() && val.items.length === 0 && this.$route.path.startsWith('/order/view')) {
                this.$router.push(`/order`);
            }
        },
    },
}
</script>
